import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { identityResetPasswordStart } from '@redux/identity/identitySlice';

const readSearchParam = (search: string, key: string): string => {
 if (!search || search === '?') return '';
 const normalized = search.startsWith('?') ? search.slice(1) : search;
 const prefixed = `&${key}=`;
 let valueStart: number;
 const prefixedIdx = normalized.indexOf(prefixed);
 if (prefixedIdx !== -1) {
  valueStart = prefixedIdx + prefixed.length;
 } else if (normalized.startsWith(`${key}=`)) {
  valueStart = key.length + 1;
 } else {
  return '';
 }
 const ampIdx = normalized.indexOf('&', valueStart);
 const raw = ampIdx === -1 ? normalized.slice(valueStart) : normalized.slice(valueStart, ampIdx);
 try {
  return decodeURIComponent(raw);
 } catch {
  return raw;
 }
};

export const useResetPasswordForm = () => {
 const dispatch = useAppDispatch();
 const navigate = useNavigate();
 const { search } = useLocation();
 const { t } = useTranslation();

 const email = readSearchParam(search, 'email').trim();
 const resetCode = readSearchParam(search, 'code').trim();
 const hasResetLinkParams = email.length > 0 && resetCode.length > 0;

 const [newPassword, setNewPassword] = useState('');
 const [repeatPassword, setRepeatPassword] = useState('');
 const [newPasswordVisible, setNewPasswordVisible] = useState(false);
 const [repeatPasswordVisible, setRepeatPasswordVisible] = useState(false);
 const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

 const { error, loading, success } = useAppSelector((state) => state.identity);
 const resetPasswordLoading = loading?.identityResetPassword === true;

 useEffect(() => {
  if (error?.identityResetPassword) {
   setErrorMessage(error.identityResetPassword.title || '');
  }
 }, [error]);

 useEffect(() => {
  if (success?.identityResetPassword) {
   navigate('/login?redirectedFromReset=true');
  }
 }, [success, navigate]);

 const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setNewPassword(e.target.value);
  setErrorMessage('');
 };

 const handleRepeatPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setRepeatPassword(e.target.value);
  setErrorMessage('');
 };

 const handleToggleNewPasswordVisibility = () =>
  setNewPasswordVisible((prev) => !prev);

 const handleToggleRepeatPasswordVisibility = () =>
  setRepeatPasswordVisible((prev) => !prev);

 const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  if (!hasResetLinkParams) {
   setErrorMessage(t('resetPassword.invalidResetLink'));
   return;
  }

  if (!newPassword) {
   setErrorMessage(t('resetPassword.newPasswordRequired'));
   return;
  }
  if (!repeatPassword) {
   setErrorMessage(t('resetPassword.repeatPasswordRequired'));
   return;
  }
  if (newPassword !== repeatPassword) {
   setErrorMessage(t('resetPassword.passwordsDoNotMatch'));
   return;
  }

  setErrorMessage('');
  dispatch(
   identityResetPasswordStart({
    Email: email,
    ResetCode: resetCode,
    NewPassword: newPassword,
   }),
  );
 };

 return {
  hasResetLinkParams,
  newPassword,
  repeatPassword,
  newPasswordVisible,
  repeatPasswordVisible,
  errorMessage,
  resetPasswordLoading,
  t,
  handleNewPasswordChange,
  handleRepeatPasswordChange,
  handleToggleNewPasswordVisibility,
  handleToggleRepeatPasswordVisibility,
  handleSubmit,
 };
};
